import {
  activatePanelIn,
  collectGroups,
  collectPanelIds,
  createGroup,
  dropPanelOnGroup,
  dropPanelOnRoot,
  findGroup,
  findPanelGroup,
  insertPanel,
  isGroup,
  nextDockId,
  removeGroup,
  removePanel,
  reorderPanel,
  resizeSplit,
  type DockNode,
  type DropZone,
  type Rect,
} from './dockModel';
import { canonicalAddress, consoleRoot, isKnownAddress } from './panelCatalog';

/*
 * The workspace, as one reducer.
 *
 * Every document the operator has open is a `panel`. Panels live either in the dock
 * tree — tabbed into a pane, panes divided by splitters — or inside a floating window
 * that can be moved, resized, rolled down to the strip along the bottom, or blown up
 * to fill the canvas. A panel exists in exactly one of those two places, never both,
 * which is what makes docking and undocking a move rather than a copy.
 */

/** Mounted documents are live pages holding live queries; past this many, the coldest one is retired. */
export const maximumPanels = 18;

export type FloatingState = 'normal' | 'minimized' | 'maximized';

export interface WorkspacePanelState {
  readonly id: string;
  /** Where the document is now — drilling into a record moves this, and the tab label with it. */
  readonly address: string;
  readonly history: readonly string[];
  readonly historyIndex: number;
  /** A pinned document keeps its place and is never retired to make room. */
  readonly pinned: boolean;
  readonly lastActiveAt: number;
}

export interface FloatingWindowState {
  readonly id: string;
  readonly panelIds: readonly string[];
  readonly activePanelId: string | null;
  readonly rect: Rect;
  /** Where a maximized window returns to. */
  readonly restoreRect: Rect | null;
  readonly state: FloatingState;
  readonly z: number;
}

export interface WorkspaceState {
  readonly panels: Readonly<Record<string, WorkspacePanelState>>;
  readonly root: DockNode;
  readonly windows: readonly FloatingWindowState[];
  /** Where a document opened from the navigation lands. Always a pane in the tree. */
  readonly activeGroupId: string;
  /** Non-null while a floating window holds the focus. */
  readonly activeWindowId: string | null;
  /** One pane blown up over the others, VS Code style; the rest stay open behind it. */
  readonly maximizedGroupId: string | null;
  readonly zCounter: number;
  /** Addresses in the order they were last opened, for the start page. */
  readonly recents: readonly string[];
  readonly clock: number;
}

export type DockTarget =
  | { readonly kind: 'group'; readonly groupId: string; readonly zone: DropZone; readonly index?: number }
  | { readonly kind: 'root'; readonly zone: Exclude<DropZone, 'center'> }
  | { readonly kind: 'window'; readonly windowId: string; readonly index?: number }
  | { readonly kind: 'float'; readonly rect: Rect };

export type WorkspaceAction =
  | { readonly type: 'open'; readonly address: string; readonly mode?: 'reuse' | 'new'; readonly groupId?: string }
  | { readonly type: 'openAt'; readonly address: string; readonly target: DockTarget }
  | { readonly type: 'activate'; readonly panelId: string }
  | { readonly type: 'focusGroup'; readonly groupId: string }
  | { readonly type: 'close'; readonly panelId: string }
  | { readonly type: 'closeOthers'; readonly panelId: string }
  | { readonly type: 'closeGroup'; readonly groupId: string }
  | { readonly type: 'closeAll' }
  | { readonly type: 'togglePin'; readonly panelId: string }
  | { readonly type: 'navigated'; readonly panelId: string; readonly address: string }
  | { readonly type: 'travel'; readonly panelId: string; readonly delta: number }
  | { readonly type: 'reorder'; readonly groupId: string; readonly panelId: string; readonly index: number }
  | { readonly type: 'movePanel'; readonly panelId: string; readonly target: DockTarget }
  | { readonly type: 'moveWindow'; readonly windowId: string; readonly rect: Rect }
  | { readonly type: 'windowState'; readonly windowId: string; readonly state: FloatingState; readonly canvas?: Rect }
  | { readonly type: 'focusWindow'; readonly windowId: string }
  | { readonly type: 'closeWindow'; readonly windowId: string }
  | { readonly type: 'dockWindow'; readonly windowId: string; readonly target: DockTarget }
  | { readonly type: 'resize'; readonly splitId: string; readonly index: number; readonly fraction: number }
  | { readonly type: 'toggleMaximize'; readonly groupId: string }
  | { readonly type: 'replace'; readonly state: WorkspaceState };

export function createWorkspaceState(addresses: readonly string[] = []): WorkspaceState {
  const panels: Record<string, WorkspacePanelState> = {};
  const ids: string[] = [];
  let clock = 1;
  for (const address of addresses) {
    const panel = createPanel(canonicalAddress(address), clock);
    clock += 1;
    panels[panel.id] = panel;
    ids.push(panel.id);
  }
  const group = createGroup(ids, ids[ids.length - 1] ?? null);
  return {
    panels,
    root: group,
    windows: [],
    activeGroupId: group.id,
    activeWindowId: null,
    maximizedGroupId: null,
    zCounter: 1,
    recents: [...addresses],
    clock,
  };
}

function createPanel(address: string, clock: number): WorkspacePanelState {
  return {
    id: nextDockId('doc'),
    address,
    history: [address],
    historyIndex: 0,
    pinned: false,
    lastActiveAt: clock,
  };
}

export function activePanelId(state: WorkspaceState): string | null {
  if (state.activeWindowId !== null) {
    const window = state.windows.find((candidate) => candidate.id === state.activeWindowId);
    if (window !== undefined && window.state !== 'minimized') return window.activePanelId;
  }
  return findGroup(state.root, state.activeGroupId)?.activePanelId ?? null;
}

export function activeAddress(state: WorkspaceState): string | null {
  const id = activePanelId(state);
  return id === null ? null : state.panels[id]?.address ?? null;
}

export function panelHostId(state: WorkspaceState, panelId: string): string | null {
  const group = findPanelGroup(state.root, panelId);
  if (group !== undefined) return group.id;
  return state.windows.find((window) => window.panelIds.includes(panelId))?.id ?? null;
}

export function findPanelByAddress(state: WorkspaceState, address: string): WorkspacePanelState | undefined {
  return Object.values(state.panels).find((panel) => panel.address === address);
}

/** Documents that have left both the tree and every window are gone; their state goes with them. */
function prunePanels(state: WorkspaceState): WorkspaceState {
  const live = new Set([...collectPanelIds(state.root), ...state.windows.flatMap((window) => [...window.panelIds])]);
  const panels: Record<string, WorkspacePanelState> = {};
  for (const [id, panel] of Object.entries(state.panels)) {
    if (live.has(id)) panels[id] = panel;
  }
  const windows = state.windows.filter((window) => window.panelIds.length > 0);
  const activeWindowId = windows.some((window) => window.id === state.activeWindowId) ? state.activeWindowId : null;
  const activeGroupId = findGroup(state.root, state.activeGroupId) === undefined
    ? collectGroups(state.root)[0]?.id ?? state.activeGroupId
    : state.activeGroupId;
  const maximizedGroupId = state.maximizedGroupId !== null && findGroup(state.root, state.maximizedGroupId) === undefined
    ? null
    : state.maximizedGroupId;
  return { ...state, panels, windows, activeWindowId, activeGroupId, maximizedGroupId };
}

/** Keeps the mounted-document count in hand by retiring the coldest unpinned, inactive tab. */
function enforcePanelBudget(state: WorkspaceState, keepPanelId: string): WorkspaceState {
  const ids = Object.keys(state.panels);
  if (ids.length <= maximumPanels) return state;
  const protectedIds = new Set([keepPanelId, ...collectGroups(state.root).map((group) => group.activePanelId ?? ''), ...state.windows.map((window) => window.activePanelId ?? '')]);
  const candidates = ids
    .map((id) => state.panels[id])
    .filter((panel): panel is WorkspacePanelState => panel !== undefined && !panel.pinned && !protectedIds.has(panel.id))
    .sort((left, right) => left.lastActiveAt - right.lastActiveAt);
  let next = state;
  for (const candidate of candidates) {
    if (Object.keys(next.panels).length <= maximumPanels) break;
    next = closePanel(next, candidate.id);
  }
  return next;
}

function touch(state: WorkspaceState, panelId: string): WorkspaceState {
  const panel = state.panels[panelId];
  if (panel === undefined) return state;
  const clock = state.clock + 1;
  return { ...state, clock, panels: { ...state.panels, [panelId]: { ...panel, lastActiveAt: clock } } };
}

function closePanel(state: WorkspaceState, panelId: string): WorkspaceState {
  const inTree = findPanelGroup(state.root, panelId) !== undefined;
  if (inTree) return prunePanels({ ...state, root: removePanel(state.root, panelId) });

  const windows = state.windows.map((window) => {
    if (!window.panelIds.includes(panelId)) return window;
    const panelIds = window.panelIds.filter((id) => id !== panelId);
    const activeIndex = Math.min(window.panelIds.indexOf(panelId), panelIds.length - 1);
    return {
      ...window,
      panelIds,
      activePanelId: window.activePanelId === panelId ? panelIds[Math.max(activeIndex, 0)] ?? null : window.activePanelId,
    };
  });
  return prunePanels({ ...state, windows });
}

function detachPanel(state: WorkspaceState, panelId: string): WorkspaceState {
  const windows = state.windows
    .map((window) => {
      if (!window.panelIds.includes(panelId)) return window;
      const panelIds = window.panelIds.filter((id) => id !== panelId);
      return {
        ...window,
        panelIds,
        activePanelId: window.activePanelId === panelId ? panelIds[panelIds.length - 1] ?? null : window.activePanelId,
      };
    })
    .filter((window) => window.panelIds.length > 0);
  return { ...state, windows, root: removePanel(state.root, panelId) };
}

function focusWindow(state: WorkspaceState, windowId: string): WorkspaceState {
  const zCounter = state.zCounter + 1;
  return {
    ...state,
    zCounter,
    activeWindowId: windowId,
    windows: state.windows.map((window) => (window.id === windowId ? { ...window, z: zCounter, state: window.state === 'minimized' ? 'normal' : window.state } : window)),
  };
}

function placePanel(state: WorkspaceState, panelId: string, target: DockTarget): WorkspaceState {
  const detached = detachPanel(state, panelId);

  if (target.kind === 'float') {
    const zCounter = detached.zCounter + 1;
    const window: FloatingWindowState = {
      id: nextDockId('window'),
      panelIds: [panelId],
      activePanelId: panelId,
      rect: target.rect,
      restoreRect: null,
      state: 'normal',
      z: zCounter,
    };
    return prunePanels({ ...detached, zCounter, windows: [...detached.windows, window], activeWindowId: window.id });
  }

  if (target.kind === 'window') {
    const zCounter = detached.zCounter + 1;
    return prunePanels({
      ...detached,
      zCounter,
      activeWindowId: target.windowId,
      windows: detached.windows.map((window) => {
        if (window.id !== target.windowId) return window;
        const panelIds = [...window.panelIds];
        const at = target.index === undefined ? panelIds.length : Math.max(0, Math.min(target.index, panelIds.length));
        panelIds.splice(at, 0, panelId);
        return { ...window, panelIds, activePanelId: panelId, z: zCounter, state: window.state === 'minimized' ? 'normal' : window.state };
      }),
    });
  }

  if (target.kind === 'root') {
    const root = dropPanelOnRoot(detached.root, panelId, target.zone);
    const owner = findPanelGroup(root, panelId);
    return prunePanels({ ...detached, root, activeWindowId: null, activeGroupId: owner?.id ?? detached.activeGroupId, maximizedGroupId: null });
  }

  const root = target.index === undefined
    ? dropPanelOnGroup(detached.root, target.groupId, panelId, target.zone)
    : dropPanelOnGroup(detached.root, target.groupId, panelId, target.zone, target.index);
  const owner = findPanelGroup(root, panelId);
  return prunePanels({ ...detached, root, activeWindowId: null, activeGroupId: owner?.id ?? detached.activeGroupId });
}

export function workspaceReducer(state: WorkspaceState, action: WorkspaceAction): WorkspaceState {
  switch (action.type) {
    case 'replace':
      return action.state;

    case 'open': {
      const address = canonicalAddress(action.address);
      if (action.mode !== 'new') {
        const existing = findPanelByAddress(state, address);
        if (existing !== undefined) return workspaceReducer(state, { type: 'activate', panelId: existing.id });
      }
      const clock = state.clock + 1;
      const panel = createPanel(address, clock);
      const groupId = action.groupId ?? state.activeGroupId;
      const targetGroup = findGroup(state.root, groupId) ?? collectGroups(state.root)[0];
      if (targetGroup === undefined) return state;
      const opened: WorkspaceState = {
        ...state,
        clock,
        panels: { ...state.panels, [panel.id]: panel },
        root: insertPanel(state.root, targetGroup.id, panel.id),
        activeGroupId: targetGroup.id,
        activeWindowId: null,
        recents: [address, ...state.recents.filter((entry) => entry !== address)].slice(0, 12),
      };
      return enforcePanelBudget(opened, panel.id);
    }

    case 'openAt': {
      // "Open beside" and "open in a window": the document is created and placed in one
      // step, because a tab that appeared in the current pane first and jumped afterwards
      // reads as a glitch rather than as a deliberate arrangement.
      const address = canonicalAddress(action.address);
      const clock = state.clock + 1;
      const panel = createPanel(address, clock);
      const seeded: WorkspaceState = {
        ...state,
        clock,
        panels: { ...state.panels, [panel.id]: panel },
        recents: [address, ...state.recents.filter((entry) => entry !== address)].slice(0, 12),
      };
      return enforcePanelBudget(placePanel(seeded, panel.id, action.target), panel.id);
    }

    case 'activate': {
      const inTree = findPanelGroup(state.root, action.panelId);
      if (inTree !== undefined) {
        return touch({ ...state, root: activatePanelIn(state.root, action.panelId), activeGroupId: inTree.id, activeWindowId: null }, action.panelId);
      }
      const window = state.windows.find((candidate) => candidate.panelIds.includes(action.panelId));
      if (window === undefined) return state;
      const focused = focusWindow(state, window.id);
      return touch({
        ...focused,
        windows: focused.windows.map((candidate) => (candidate.id === window.id ? { ...candidate, activePanelId: action.panelId } : candidate)),
      }, action.panelId);
    }

    case 'focusGroup':
      if (findGroup(state.root, action.groupId) === undefined) return state;
      return { ...state, activeGroupId: action.groupId, activeWindowId: null };

    case 'close':
      return closePanel(state, action.panelId);

    case 'closeOthers': {
      const host = panelHostId(state, action.panelId);
      if (host === null) return state;
      const group = findGroup(state.root, host);
      const siblings = group?.panelIds ?? state.windows.find((window) => window.id === host)?.panelIds ?? [];
      let next = state;
      for (const id of siblings) {
        if (id === action.panelId) continue;
        if (next.panels[id]?.pinned === true) continue;
        next = closePanel(next, id);
      }
      return next;
    }

    case 'closeGroup': {
      const group = findGroup(state.root, action.groupId);
      if (group === undefined) return state;
      const keep = group.panelIds.filter((id) => state.panels[id]?.pinned === true);
      if (keep.length > 0) {
        let next = state;
        for (const id of group.panelIds) {
          if (keep.includes(id)) continue;
          next = closePanel(next, id);
        }
        return next;
      }
      return prunePanels({ ...state, root: removeGroup(state.root, action.groupId) });
    }

    case 'closeAll': {
      let next = state;
      for (const id of Object.keys(state.panels)) {
        if (state.panels[id]?.pinned === true) continue;
        next = closePanel(next, id);
      }
      return next;
    }

    case 'togglePin': {
      const panel = state.panels[action.panelId];
      if (panel === undefined) return state;
      return { ...state, panels: { ...state.panels, [action.panelId]: { ...panel, pinned: !panel.pinned } } };
    }

    case 'navigated': {
      const panel = state.panels[action.panelId];
      const address = canonicalAddress(action.address);
      if (panel === undefined || panel.address === address) return state;
      const back = panel.history[panel.historyIndex - 1] === address;
      const forward = panel.history[panel.historyIndex + 1] === address;
      const history = back || forward ? panel.history : [...panel.history.slice(0, panel.historyIndex + 1), address].slice(-30);
      const historyIndex = back ? panel.historyIndex - 1 : forward ? panel.historyIndex + 1 : history.length - 1;
      return {
        ...state,
        panels: { ...state.panels, [action.panelId]: { ...panel, address, history, historyIndex } },
        recents: [address, ...state.recents.filter((entry) => entry !== address)].slice(0, 12),
      };
    }

    case 'travel': {
      // The workspace keeps each document's trail itself, because the browser has one
      // history and the workspace has as many as it has tabs.
      const panel = state.panels[action.panelId];
      if (panel === undefined) return state;
      const historyIndex = panel.historyIndex + action.delta;
      const address = panel.history[historyIndex];
      if (address === undefined) return state;
      return { ...state, panels: { ...state.panels, [action.panelId]: { ...panel, address, historyIndex } } };
    }

    case 'reorder':
      return { ...state, root: reorderPanel(state.root, action.groupId, action.panelId, action.index) };

    case 'movePanel':
      return placePanel(state, action.panelId, action.target);

    case 'moveWindow':
      return {
        ...state,
        windows: state.windows.map((window) => (window.id === action.windowId ? { ...window, rect: action.rect } : window)),
      };

    case 'windowState': {
      const canvas = action.canvas;
      return {
        ...state,
        windows: state.windows.map((window) => {
          if (window.id !== action.windowId) return window;
          if (action.state === 'maximized' && canvas !== undefined) {
            return { ...window, state: 'maximized', restoreRect: window.restoreRect ?? window.rect, rect: canvas };
          }
          if (action.state === 'normal' && window.state === 'maximized' && window.restoreRect !== null) {
            return { ...window, state: 'normal', rect: window.restoreRect, restoreRect: null };
          }
          return { ...window, state: action.state };
        }),
        activeWindowId: action.state === 'minimized' && state.activeWindowId === action.windowId ? null : state.activeWindowId,
      };
    }

    case 'focusWindow':
      return focusWindow(state, action.windowId);

    case 'closeWindow': {
      const window = state.windows.find((candidate) => candidate.id === action.windowId);
      if (window === undefined) return state;
      let next = state;
      for (const id of window.panelIds) next = closePanel(next, id);
      return next;
    }

    case 'dockWindow': {
      const window = state.windows.find((candidate) => candidate.id === action.windowId);
      if (window === undefined) return state;
      // Released over open canvas, a window has simply been moved, not docked. Taking it
      // apart into one window per tab would be a strange answer to "put it there".
      if (action.target.kind === 'float') {
        const rect = action.target.rect;
        return { ...state, windows: state.windows.map((candidate) => (candidate.id === window.id ? { ...candidate, rect } : candidate)) };
      }
      let next = state;
      // The active tab leads, so the pane it lands in shows what the window was showing.
      const ordered = [...window.panelIds].sort((left, right) => Number(right === window.activePanelId) - Number(left === window.activePanelId));
      for (const [index, panelId] of ordered.entries()) {
        const target: DockTarget = index === 0
          ? action.target
          : action.target.kind === 'group' || action.target.kind === 'root'
            ? { kind: 'group', groupId: panelHostId(next, ordered[0] ?? panelId) ?? next.activeGroupId, zone: 'center' }
            : action.target;
        next = placePanel(next, panelId, target);
      }
      const landed = ordered[0];
      return landed === undefined ? next : workspaceReducer(next, { type: 'activate', panelId: landed });
    }

    case 'resize':
      return { ...state, root: resizeSplit(state.root, action.splitId, action.index, action.fraction) };

    case 'toggleMaximize':
      return { ...state, maximizedGroupId: state.maximizedGroupId === action.groupId ? null : action.groupId };

    default:
      return state;
  }
}

/* ------------------------------------------------------------------ *
 * Storage
 * ------------------------------------------------------------------ */

interface StoredPanel {
  readonly id: string;
  readonly address: string;
  readonly pinned: boolean;
}

interface StoredWorkspace {
  readonly version: number;
  readonly panels: readonly StoredPanel[];
  readonly root: DockNode;
  readonly windows: readonly FloatingWindowState[];
  readonly activeGroupId: string;
}

const storageVersion = 1;

export function workspaceStorageKey(tenantId: string | null | undefined): string | null {
  return tenantId ? `varza.workspace.${tenantId}` : null;
}

export function serializeWorkspace(state: WorkspaceState): string {
  const stored: StoredWorkspace = {
    version: storageVersion,
    panels: Object.values(state.panels).map((panel) => ({ id: panel.id, address: panel.address, pinned: panel.pinned })),
    root: state.root,
    windows: state.windows,
    activeGroupId: state.activeGroupId,
  };
  return JSON.stringify(stored);
}

function isDockNode(value: unknown): value is DockNode {
  if (typeof value !== 'object' || value === null) return false;
  const node = value as { kind?: unknown; children?: unknown };
  if (node.kind === 'group') return true;
  return node.kind === 'split' && Array.isArray(node.children) && node.children.every(isDockNode);
}

/**
 * Rebuilds a stored workspace, dropping anything that no longer resolves: an address the
 * build no longer serves, a pane left holding nothing. A layout that survives none of that
 * comes back as `null`, and the caller opens the dashboard instead.
 */
export function deserializeWorkspace(raw: string): WorkspaceState | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const stored = parsed as Partial<StoredWorkspace>;
  if (stored.version !== storageVersion || !Array.isArray(stored.panels) || !isDockNode(stored.root)) return null;

  const panels: Record<string, WorkspacePanelState> = {};
  let clock = 1;
  for (const entry of stored.panels) {
    if (typeof entry?.id !== 'string' || typeof entry.address !== 'string') continue;
    const address = canonicalAddress(entry.address);
    if (!isKnownAddress(address)) continue;
    clock += 1;
    panels[entry.id] = {
      id: entry.id,
      address,
      history: [address],
      historyIndex: 0,
      pinned: entry.pinned === true,
      lastActiveAt: clock,
    };
  }

  const keep = (id: string) => panels[id] !== undefined;
  const pruneTree = (node: DockNode): DockNode | null => {
    if (isGroup(node)) {
      const panelIds = node.panelIds.filter(keep);
      if (panelIds.length === 0) return null;
      return { ...node, panelIds, activePanelId: panelIds.includes(node.activePanelId ?? '') ? node.activePanelId : panelIds[0] ?? null };
    }
    const children = node.children.map(pruneTree).filter((child): child is DockNode => child !== null);
    if (children.length === 0) return null;
    const only = children[0];
    if (children.length === 1 && only !== undefined) return only;
    return { ...node, children, sizes: children.map(() => 1 / children.length) };
  };

  const root = pruneTree(stored.root) ?? createGroup();
  const windows = (stored.windows ?? [])
    .map((window) => ({ ...window, panelIds: window.panelIds.filter(keep) }))
    .filter((window) => window.panelIds.length > 0)
    .map((window) => ({ ...window, activePanelId: window.panelIds.includes(window.activePanelId ?? '') ? window.activePanelId : window.panelIds[0] ?? null }));

  const live = new Set([...collectPanelIds(root), ...windows.flatMap((window) => [...window.panelIds])]);
  for (const id of Object.keys(panels)) {
    if (!live.has(id)) delete panels[id];
  }
  if (live.size === 0) return null;

  const groups = collectGroups(root);
  const activeGroupId = groups.some((group) => group.id === stored.activeGroupId) ? stored.activeGroupId : groups[0]?.id;
  if (activeGroupId === undefined) return null;

  return {
    panels,
    root,
    windows,
    activeGroupId,
    activeWindowId: null,
    maximizedGroupId: null,
    zCounter: windows.reduce((highest, window) => Math.max(highest, window.z), 1),
    recents: Object.values(panels).map((panel) => panel.address).slice(0, 12),
    clock,
  };
}

export const dashboardAddress = consoleRoot;
